import { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';

const ChatOnboardingHighlight = ({ targetSelector, isActive, padding = 8, borderRadius = 8, children }) => {
  const [targetBounds, setTargetBounds] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const overlayRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const updateBounds = useCallback(() => {
    if (targetSelector && isActive) {
      try {
        const element = document.querySelector(targetSelector);
        if (element) {
          const rect = element.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

          setTargetBounds({
            top: rect.top + scrollTop - padding,
            left: rect.left + scrollLeft - padding,
            width: rect.width + (padding * 2),
            height: rect.height + (padding * 2),
          });

          // Scroll behavior for chat interface elements
          const windowHeight = window.innerHeight;
          const elementTop = rect.top;
          const elementBottom = rect.bottom;

          if (isMobile) {
            // For mobile chat, ensure element is visible above the onboarding card
            const cardHeight = 200; // Smaller mobile card height
            const safeViewportHeight = windowHeight - cardHeight - 20;

            // Check if element is visible in the safe area
            const isVisible = elementTop > 20 && elementBottom < safeViewportHeight;

            if (!isVisible) {
              setTimeout(() => {
                try {
                  // Scroll to position element in the middle of the safe area
                  const targetPosition = element.getBoundingClientRect().top + window.scrollY - (safeViewportHeight / 2) + (rect.height / 2);
                  window.scrollTo({
                    top: targetPosition,
                    behavior: 'smooth'
                  });
                } catch (scrollError) {
                  // Silently handle scroll errors
                }
              }, 300);
            }
          } else {
            // Desktop behavior - center element in viewport
            const isVisible = elementTop > windowHeight * 0.1 && elementBottom < windowHeight * 0.9;

            if (!isVisible) {
              setTimeout(() => {
                try {
                  element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest'
                  });
                } catch (scrollError) {
                  // Silently handle scroll errors
                }
              }, 200);
            }
          }
        } else {
          setTargetBounds(null);
        }
      } catch (error) {
        // Silently handle errors in production
        setTargetBounds(null);
      }
    } else if (!targetSelector && isActive) {
      setTargetBounds(null);
    }
  }, [targetSelector, isActive, padding, isMobile]);

  useEffect(() => {
    // Initial update
    updateBounds();

    // Only update on significant events
    const handleResize = () => {
      requestAnimationFrame(updateBounds);
    };

    const handleScroll = () => {
      requestAnimationFrame(updateBounds);
    };

    if (isActive) {
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, { passive: true });

      // Also listen to scroll events on the message container
      const messageContainer = document.querySelector('.message-list-container');
      if (messageContainer) {
        messageContainer.addEventListener('scroll', handleScroll, { passive: true });
      }

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll);
        if (messageContainer) {
          messageContainer.removeEventListener('scroll', handleScroll);
        }
      };
    }
  }, [updateBounds, isActive]);

  if (!isActive) return null;

  const overlayContent = (
    <>
      <div
        ref={overlayRef}
        className="fixed inset-0 z-[9998] transition-opacity duration-300"
        style={{
          pointerEvents: 'none',
          opacity: isActive ? 1 : 0
        }}
      >
        {targetBounds ? (
          <svg
            className="absolute inset-0 w-full h-full"
            style={{ pointerEvents: 'none' }}
          >
            <defs>
              <mask id="chat-spotlight-mask">
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                <motion.rect
                  x={targetBounds.left}
                  y={targetBounds.top - window.pageYOffset}
                  width={targetBounds.width}
                  height={targetBounds.height}
                  rx={borderRadius}
                  fill="black"
                  initial={false}
                  animate={{
                    x: targetBounds.left,
                    y: targetBounds.top - window.pageYOffset,
                    width: targetBounds.width,
                    height: targetBounds.height
                  }}
                  transition={{
                    duration: 0.5,
                    ease: [0.32, 0.72, 0, 1]
                  }}
                />
              </mask>
              <filter id="chat-glow">
                <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              mask="url(#chat-spotlight-mask)"
              className="fill-black/90"
            />
            <motion.rect
              x={targetBounds.left - 2}
              y={targetBounds.top - window.pageYOffset - 2}
              width={targetBounds.width + 4}
              height={targetBounds.height + 4}
              rx={borderRadius}
              fill="none"
              stroke="url(#chatBorderGradient)"
              strokeWidth="2"
              filter="url(#chat-glow)"
              initial={false}
              animate={{
                x: targetBounds.left - 2,
                y: targetBounds.top - window.pageYOffset - 2,
                width: targetBounds.width + 4,
                height: targetBounds.height + 4
              }}
              transition={{
                duration: 0.5,
                ease: [0.32, 0.72, 0, 1]
              }}
            />
            <defs>
              <linearGradient id="chatBorderGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.8" />
                <stop offset="50%" stopColor="#10b981" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.8" />
              </linearGradient>
            </defs>
          </svg>
        ) : (
          <div className="absolute inset-0 bg-black/90 transition-opacity duration-300" />
        )}
      </div>

      <div
        className="fixed z-[9999]"
        style={{ pointerEvents: 'auto' }}
      >
        {children}
      </div>
    </>
  );

  return createPortal(overlayContent, document.body);
};

export default ChatOnboardingHighlight;